import { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import ContactManagerPanel, { type ContactManagerItem } from "@/components/contacts/ContactManagerPanel";
import SupplierCategoryDialog from "@/components/suppliers/SupplierCategoryDialog";
import SupplierContractsPanel from "@/components/suppliers/SupplierContractsPanel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { COUNTRY_OPTIONS } from "@/lib/countries";
import {
  CONTRACT_ALERT_LABELS,
  CONTRACT_ALERT_TONES,
  createSupplier,
  deleteSupplier,
  getSupplier,
  listContractAlerts,
  listSupplierCategories,
  listSuppliers,
  updateSupplier,
  type Supplier,
  type SupplierCategory,
  type SupplierContract,
  type SupplierSummary,
} from "@/lib/suppliersApi";
import {
  AlertTriangle,
  Building2,
  Globe,
  Loader2,
  Mail,
  Phone,
  Plus,
  Search,
  Settings2,
  Trash2,
} from "lucide-react";

const PAGE_SIZE = 10;
const ALL = "__all__";

type SupplierFormState = {
  name: string;
  country: string;
  address: string;
  website: string;
  notes: string;
  categoryIds: string[];
  contacts: ContactManagerItem[];
};

const emptyForm: SupplierFormState = {
  name: "",
  country: "",
  address: "",
  website: "",
  notes: "",
  categoryIds: [],
  contacts: [],
};

const Suppliers = () => {
  const { toast } = useToast();
  const { userRole } = useAuth();
  // Everyone can add and edit a supplier; only CEO and General Manager can remove one.
  const canDelete = userRole === "ceo" || userRole === "general_manager";
  const isAlertedRole = canDelete;

  const [items, setItems] = useState<SupplierSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [countries, setCountries] = useState<string[]>([]);
  const [categories, setCategories] = useState<SupplierCategory[]>([]);
  const [alerts, setAlerts] = useState<SupplierContract[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState(ALL);
  const [categoryFilter, setCategoryFilter] = useState(ALL);
  const [page, setPage] = useState(1);

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<SupplierFormState>(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listSuppliers({
        search: search.trim() || undefined,
        country: countryFilter === ALL ? undefined : countryFilter,
        categoryId: categoryFilter === ALL ? undefined : categoryFilter,
        page,
        size: PAGE_SIZE,
        sortField: "name",
        sortOrder: "asc",
      });
      setItems(response.items);
      setTotal(response.total);
      setCountries(response.countries);
    } catch (error: any) {
      toast({
        title: "Unable to load suppliers",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [search, countryFilter, categoryFilter, page, toast]);

  const loadCategories = useCallback(async () => {
    try {
      setCategories(await listSupplierCategories());
    } catch {
      // Non-fatal: the page still works without the category filter populated.
    }
  }, []);

  const loadAlerts = useCallback(async () => {
    if (!isAlertedRole) return;
    try {
      const response = await listContractAlerts();
      setAlerts(response.items);
    } catch {
      // Non-fatal.
    }
  }, [isAlertedRole]);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => { void loadSuppliers(); }, 300);
    return () => clearTimeout(handle);
  }, [loadSuppliers]);

  useEffect(() => {
    void loadCategories();
    void loadAlerts();
  }, [loadCategories, loadAlerts]);

  useEffect(() => {
    setPage(1);
  }, [search, countryFilter, categoryFilter]);

  const countryItems = useMemo(
    () => COUNTRY_OPTIONS.map((option) => ({ id: option.title, title: option.title })),
    [],
  );

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = async (id: number) => {
    try {
      const supplier = await getSupplier(id);
      setForm({
        name: supplier.name ?? "",
        country: supplier.country ?? "",
        address: supplier.address ?? "",
        website: supplier.website ?? "",
        notes: supplier.notes ?? "",
        categoryIds: supplier.category_ids ?? [],
        contacts: (supplier.contacts ?? []) as ContactManagerItem[],
      });
      setEditingId(id);
      setFormOpen(true);
    } catch (error: any) {
      toast({
        title: "Unable to open supplier",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || saving) return;
    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        country: form.country.trim() || null,
        address: form.address.trim() || null,
        website: form.website.trim() || null,
        notes: form.notes.trim() || null,
        category_ids: form.categoryIds,
        contacts: form.contacts.map((contact) => ({
          name: contact.name ?? null,
          position: contact.position ?? null,
          email: contact.email ?? null,
          telephone: contact.telephone ?? null,
          is_primary: Boolean(contact.is_primary),
        })),
      };
      if (editingId) {
        await updateSupplier(editingId, payload);
        toast({ title: "Supplier updated" });
      } else {
        const created = await createSupplier(payload);
        setEditingId(created.id);
        toast({ title: "Supplier created", description: "You can attach contracts now." });
      }
      await loadSuppliers();
      await loadCategories();
    } catch (error: any) {
      toast({
        title: editingId ? "Unable to update supplier" : "Unable to create supplier",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (supplier: SupplierSummary) => {
    if (!window.confirm(`Delete "${supplier.name}"? This cannot be undone.`)) return;
    try {
      await deleteSupplier(supplier.id);
      toast({ title: "Supplier deleted" });
      await loadSuppliers();
      await loadCategories();
    } catch (error: any) {
      toast({
        title: "Unable to delete supplier",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    }
  };

  const toggleCategory = (categoryId: string) => {
    setForm((prev) => ({
      ...prev,
      categoryIds: prev.categoryIds.includes(categoryId)
        ? prev.categoryIds.filter((id) => id !== categoryId)
        : [...prev.categoryIds, categoryId],
    }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Suppliers</h1>
            <p className="text-sm text-muted-foreground">
              Contact directory and contracts for the vendors High Class works with.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(true)}>
              <Settings2 className="mr-2 h-4 w-4" />
              Categories
            </Button>
            <Button type="button" onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              New Supplier
            </Button>
          </div>
        </div>

        {isAlertedRole && alerts.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-amber-900">
                  {alerts.length === 1
                    ? "1 contract is expiring"
                    : `${alerts.length} contracts are expiring`}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {alerts.map((contract) => (
                    <Badge
                      key={contract.id}
                      variant="outline"
                      className={contract.alert_threshold ? CONTRACT_ALERT_TONES[contract.alert_threshold] : ""}
                    >
                      {contract.supplier_name}
                      {contract.alert_threshold ? ` · ${CONTRACT_ALERT_LABELS[contract.alert_threshold]}` : ""}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Directory</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-[1fr_200px_200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by company, contact, country..."
                  className="pl-9"
                />
              </div>
              <Select value={countryFilter} onValueChange={setCountryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All countries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All countries</SelectItem>
                  {countries.map((country) => (
                    <SelectItem key={country} value={country}>{country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Categories</TableHead>
                    <TableHead>Primary contact</TableHead>
                    <TableHead className="text-center">Contracts</TableHead>
                    <TableHead className="w-16 text-right">{canDelete ? "Actions" : ""}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                        Loading suppliers...
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                        No suppliers found.
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && items.map((supplier) => (
                    <TableRow
                      key={supplier.id}
                      onClick={() => { void openEdit(supplier.id); }}
                      className="cursor-pointer"
                    >
                      <TableCell>
                        <div className="flex items-center gap-2 font-medium">
                          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                          {supplier.name}
                        </div>
                        {supplier.website && (
                          <a
                            href={supplier.website}
                            target="_blank"
                            rel="noreferrer noopener"
                            onClick={(event) => event.stopPropagation()}
                            className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground hover:underline"
                          >
                            <Globe className="h-3 w-3" />
                            {supplier.website.replace(/^https?:\/\//, "")}
                          </a>
                        )}
                      </TableCell>
                      <TableCell>{supplier.country || "—"}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(supplier.categories ?? []).length === 0 && <span className="text-muted-foreground">—</span>}
                          {(supplier.categories ?? []).map((category) => (
                            <Badge key={category.id} variant="secondary" className="text-[11px]">
                              {category.name}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {supplier.primary_contact ? (
                          <div className="space-y-0.5 text-sm">
                            <p className="font-medium">{supplier.primary_contact.name}</p>
                            {supplier.primary_contact.position && (
                              <p className="text-xs text-muted-foreground">{supplier.primary_contact.position}</p>
                            )}
                            {supplier.primary_contact.email && (
                              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Mail className="h-3 w-3" />
                                {supplier.primary_contact.email}
                              </p>
                            )}
                            {supplier.primary_contact.telephone && (
                              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Phone className="h-3 w-3" />
                                {supplier.primary_contact.telephone}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={supplier.contract_count > 0 ? "default" : "secondary"}>
                          {supplier.contract_count}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {canDelete && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            title="Delete"
                            className="text-destructive hover:text-destructive"
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleDelete(supplier);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Page {page} of {totalPages} · {total} supplier{total === 1 ? "" : "s"}
                </span>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <Button type="button" variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit supplier" : "New supplier"}</DialogTitle>
            <DialogDescription>
              {editingId
                ? "Update the company details, contacts and contracts."
                : "Save the company first, then attach its contracts."}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="details">
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="contacts">Contacts</TabsTrigger>
              <TabsTrigger value="contracts" disabled={!editingId}>
                Contracts
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 pt-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="supplier-name">Company name *</Label>
                  <Input
                    id="supplier-name"
                    value={form.name}
                    onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                    placeholder="Jumeirah Group"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Country</Label>
                  <SearchableSelect
                    items={countryItems}
                    value={form.country}
                    onValueChange={(value) => setForm((prev) => ({ ...prev, country: String(value) }))}
                    placeholder="Select a country"
                    fallbackDisplayValue={form.country}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="supplier-website">Website</Label>
                  <Input
                    id="supplier-website"
                    value={form.website}
                    onChange={(event) => setForm((prev) => ({ ...prev, website: event.target.value }))}
                    placeholder="jumeirah.com"
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="supplier-address">Address</Label>
                  <Input
                    id="supplier-address"
                    value={form.address}
                    onChange={(event) => setForm((prev) => ({ ...prev, address: event.target.value }))}
                    placeholder="Street, city"
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <div className="flex items-center justify-between">
                    <Label>Categories</Label>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setCategoryDialogOpen(true)}>
                      <Settings2 className="mr-2 h-3.5 w-3.5" />
                      Manage
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 rounded-lg border p-3">
                    {categories.length === 0 && (
                      <span className="text-sm text-muted-foreground">
                        No categories yet — create one with "Manage".
                      </span>
                    )}
                    {categories.map((category) => {
                      const selected = form.categoryIds.includes(category.id);
                      return (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => toggleCategory(category.id)}
                          className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                            selected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-input hover:bg-muted"
                          }`}
                        >
                          {category.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="supplier-notes">Notes</Label>
                  <Textarea
                    id="supplier-notes"
                    value={form.notes}
                    onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                    rows={4}
                    placeholder="Negotiated rates, payment terms, anything worth remembering."
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="contacts" className="pt-4">
              <ContactManagerPanel
                contacts={form.contacts}
                onChange={(nextContacts) => setForm((prev) => ({ ...prev, contacts: nextContacts }))}
              />
              <p className="mt-3 text-xs text-muted-foreground">
                The contact starred as priority is shown as the primary contact in the directory.
              </p>
            </TabsContent>

            <TabsContent value="contracts" className="pt-4">
              {editingId ? (
                <SupplierContractsPanel
                  supplierId={editingId}
                  canDelete={canDelete}
                  onContractsChanged={() => { void loadSuppliers(); void loadAlerts(); }}
                />
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  Save the supplier first to attach contracts.
                </p>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
              Close
            </Button>
            <Button type="button" onClick={() => { void handleSave(); }} disabled={!form.name.trim() || saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingId ? "Save changes" : "Create supplier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SupplierCategoryDialog
        open={categoryDialogOpen}
        onOpenChange={setCategoryDialogOpen}
        canDelete={canDelete}
        onCategoriesChanged={() => { void loadCategories(); void loadSuppliers(); }}
      />
    </DashboardLayout>
  );
};

export default Suppliers;
